<template>
    <div>
        <!-- While collapsed, tapping the faded text itself expands it - the
             fade reads as "there is more here", so the text answers that
             directly instead of making the guest find the link. A tap on a
             real link inside the prose still follows the link. -->
        <div
            ref="body"
            :class="{ 'truncated-content': truncatable && !expanded, 'truncated-content-clip': clipped }"
            :style="bodyStyle"
            @click="onBodyClick"
        >
            <slot />
        </div>

        <!-- A text link, not a pill button: the toggle is optional reading,
             and an outlined button under every section read as a step the
             guest had to take. The label says WHAT is hidden ("Read the full
             description") rather than a generic "Show more", which on 20 Oct
             2026 read as a stray line of body text. The chevron turns over
             rather than swapping icon. -->
        <button
            v-if="truncatable"
            type="button"
            class="truncated-content-toggle"
            :aria-expanded="expanded ? 'true' : 'false'"
            @click.prevent="toggle"
        >
            <span>{{ expanded ? 'Show less' : moreLabel }}</span>

            <span
                class="lh-transition"
                :class="{ 'rotate-180': expanded }"
                v-html="chevronDown"
            ></span>
        </button>
    </div>
</template>

<script setup>
    import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
    import chevronDown from '@img/icons/chevron-down.svg?raw';
    import { pushToDataLayer } from '@js/src/dataLayer';

    const props = defineProps({
        // Which section this is ('description', 'location', 'weddings'), sent
        // with the show_more_click event so GA4 can split them.
        section: {
            type: String,
            default: '',
        },
    });

    const body = ref(null);
    const expanded = ref(false);
    const tall = ref(false);
    const wide = ref(false);
    const animating = ref(false);
    const height = ref(0);
    const cut = ref(320);
    const fade = ref(110);

    // The collapsed height is chosen per section from where its paragraphs end
    // (chooseCut), so the fade lands on a natural pause rather than half way
    // through a sentence. FALLBACK_CUT is used when no paragraph ends between
    // MIN_CUT and MAX_CUT (one very long opening paragraph). SLACK
    // keeps content that is only a little taller than the cut in full - a
    // toggle hiding two lines is worse than no truncation.
    const MIN_CUT = 220;
    const MAX_CUT = 400;
    const FALLBACK_CUT = 320;
    const SLACK = 80;
    const DURATION = 320;

    // No collapsing from the lg breakpoint (--breakpoint-lg, 769px) up. From
    // there the enquiry form sits in the sticky sidebar beside the prose, so
    // hiding text shortens nothing the guest needs to reach - it only hides
    // the description. Read live, so resizing across the breakpoint switches
    // the toggle on and off.
    const WIDE_QUERY = '(min-width: 769px)';

    const MORE_LABELS = {
        description: 'Read the full description',
        location: 'More about the area',
        weddings: 'More about weddings',
    };

    const truncatable = computed(() => tall.value && !wide.value);

    const moreLabel = computed(() => MORE_LABELS[props.section] || 'Read more');

    // Clipped while collapsed and for the length of either animation.
    const clipped = computed(() => truncatable.value && (!expanded.value || animating.value));

    const bodyStyle = computed(() => {
        if (!clipped.value) {
            return {};
        }

        return {
            '--truncated-fade': `${fade.value}px`,
            maxHeight: `${animating.value ? height.value : cut.value}px`,
            transition: animating.value && !reducedMotion() ? `max-height ${DURATION}ms ease` : 'none',
        };
    });

    let mediaQuery = null;
    let timer = null;

    function reducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    // Where to stop: the last paragraph that ENDS inside the band is shown in
    // full, and the opening two lines of the next one fade out beneath it. So
    // everything readable is whole, and what fades is plainly the start of
    // more. Returns the cut and how tall the fade should be.
    function chooseCut() {
        const prose = body.value?.firstElementChild;
        const blocks = prose ? [...prose.children] : [];
        const top = body.value.getBoundingClientRect().top;

        for (let i = blocks.length - 2; i >= 0; i--) {
            const end = blocks[i].getBoundingClientRect().bottom - top;

            if (end < MIN_CUT || end > MAX_CUT) {
                continue;
            }

            const next = blocks[i + 1];
            const line = parseFloat(getComputedStyle(next).lineHeight) || 26;
            const start = next.getBoundingClientRect().top - top;

            return { at: Math.round(start + line * 2), fade: Math.round(start + line * 2 - end) };
        }

        return { at: FALLBACK_CUT, fade: 110 };
    }

    function measure() {
        if (!body.value) {
            return;
        }

        // scrollHeight is the full content height even while max-height clips
        // it, so this is right in either state.
        const chosen = chooseCut();
        cut.value = chosen.at;
        fade.value = chosen.fade;
        tall.value = body.value.scrollHeight > cut.value + SLACK;
    }

    function onMediaChange(event) {
        wide.value = event.matches;
        measure();
    }

    function animate(from, to) {
        clearTimeout(timer);
        height.value = from;
        animating.value = true;

        // Two frames: one to paint the starting height, one to move it, so the
        // transition has something to run from.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                height.value = to;
            });
        });

        timer = setTimeout(() => {
            animating.value = false;
        }, reducedMotion() ? 0 : DURATION + 30);
    }

    function toggle() {
        expanded.value = !expanded.value;

        if (expanded.value) {
            animate(cut.value, body.value.scrollHeight);
        } else {
            animate(body.value.scrollHeight, cut.value);

            // Closing a long section leaves the guest far below it, wherever
            // the rest of the page happens to land. Bring the section's top
            // back into view, under the sticky header, if it is above the
            // screen.
            nextTick(() => {
                const top = body.value.getBoundingClientRect().top;

                if (top < 0) {
                    window.scrollBy({ top: top - 110, behavior: reducedMotion() ? 'auto' : 'smooth' });
                }
            });
        }

        pushToDataLayer({
            event: 'show_more_click',
            section: props.section,
            action: expanded.value ? 'expand' : 'collapse',
        });
    }

    function onBodyClick(event) {
        if (!truncatable.value || expanded.value) {
            return;
        }

        if (event.target.closest('a')) {
            return;
        }

        toggle();
    }

    onMounted(() => {
        mediaQuery = window.matchMedia(WIDE_QUERY);
        wide.value = mediaQuery.matches;
        mediaQuery.addEventListener('change', onMediaChange);
        // Text reflows on rotation and narrower resizes, which can tip a
        // section over or under the cut, and move where its paragraphs end.
        window.addEventListener('resize', measure, { passive: true });

        measure();

        // Web fonts can land after mount and reflow the text.
        document.fonts?.ready.then(measure);
    });

    onBeforeUnmount(() => {
        clearTimeout(timer);
        mediaQuery?.removeEventListener('change', onMediaChange);
        window.removeEventListener('resize', measure);
    });
</script>
